import React, { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Timez.css';
import { useNavigate } from "react-router-dom";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const AwardWinningTimeline = () => {
  const timelineContainerRef = useRef(null);
  const particleFieldRef = useRef(null);
  const headerGlowRef = useRef(null);
  const timelineHeaderRef = useRef(null);
  const titleWordsRef = useRef([]);
  const timelineItemsRef = useRef([]);
  const timelineCardsRef = useRef([]);
  const particleInterval = useRef(null);
  const navigate = useNavigate();

  const services = [
    {
      title: "Google Ads",
      description: "Reach customers through the world's largest search engine with targeted campaigns that deliver measurable results and maximum ROI.",
      badge: "Search Engine",
      icon: "https://cdn-icons-png.flaticon.com/512/2504/2504739.png",
      path: "/google-ads"
    },
    {
      title: "Meta Ads",
      description: "Connect with your audience across Facebook & Instagram with engaging visuals that drive engagement and conversions.",
      badge: "Social Media",
      icon: "https://cdn-icons-png.flaticon.com/512/2504/2504903.png",
      path: "/meta-ads"
    },
    {
      title: "Bing Ads",
      description: "Tap into Microsoft's search network for comprehensive market coverage and reach untapped audiences effectively.",
      badge: "Microsoft",
      icon: "https://img.icons8.com/color/512/bing.png",
      path: "/bing-ads"
    },
    {
      title: "Amazon Ads",
      description: "Drive sales directly on the world's largest e-commerce platform with strategic product placement and optimization.",
      badge: "E-Commerce",
      icon: "https://img.icons8.com/color/512/amazon.png",
      path: "/amazon-ads"
    }
  ];

  // Init GSAP animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (headerGlowRef.current && timelineHeaderRef.current) {
        const headerRect = timelineHeaderRef.current.getBoundingClientRect();
        gsap.set(headerGlowRef.current, {
          x: headerRect.width / 2 - 100,
          y: headerRect.height / 2 - 100,
          opacity: 0.1
        });
      }

      if (titleWordsRef.current.length > 0) {
        gsap.fromTo(titleWordsRef.current,
          { opacity: 0, y: 100, rotationX: -90 },
          {
            opacity: 1,
            y: 0,
            rotationX: 0,
            duration: 1.2,
            stagger: 0.3,
            ease: "back.out(1.7)"
          }
        );

        titleWordsRef.current.forEach((word, index) => {
          gsap.to(word, {
            backgroundPosition: "200% 50%",
            duration: 4,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
            delay: index * 0.5
          });
        });
      }

      timelineCardsRef.current.forEach((card) => {
        if (card) {
          gsap.set(card, { opacity: 0, y: 120, scale: 0.85, rotationX: -15 });
        }
      });
    }, timelineContainerRef);

    return () => ctx.revert();
  }, []);

  // Glow pulse
  useEffect(() => {
    if (!headerGlowRef.current) return;
    const glowTl = gsap.timeline({ repeat: -1 });
    glowTl.to(headerGlowRef.current, { scale: 1.3, opacity: 0.2, duration: 2 })
          .to(headerGlowRef.current, { scale: 1, opacity: 0.1, duration: 2 });
    return () => glowTl.kill();
  }, []);

  // Glow follow mouse
  useEffect(() => {
    if (!headerGlowRef.current || !timelineHeaderRef.current) return;
    const header = timelineHeaderRef.current;
    const glow = headerGlowRef.current;
    const xTo = gsap.quickTo(glow, "x", { duration: 0.6, ease: "power3" });
    const yTo = gsap.quickTo(glow, "y", { duration: 0.6, ease: "power3" });
    const handleMouseMove = (e) => {
      const rect = header.getBoundingClientRect();
      xTo(e.clientX - rect.left - 100);
      yTo(e.clientY - rect.top - 100);
    };
    header.addEventListener('mousemove', handleMouseMove);
    return () => header.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll-triggered reveal
  useEffect(() => {
    timelineItemsRef.current.forEach((item, index) => {
      const card = timelineCardsRef.current[index];
      if (!item || !card) return;
      ScrollTrigger.create({
        trigger: item,
        start: "top 80%",
        onEnter: () => {
          gsap.to(card, { opacity: 1, y: 0, scale: 1, rotationX: 0, duration: 1.2, delay: index * 0.2, ease: "back.out(1.7)" });
        }
      });
    });
    return () => ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }, []);

  // Particles
  useEffect(() => {
    if (!particleFieldRef.current) return;
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = Math.random() * 100 + 'vw';
      particleFieldRef.current.appendChild(particle);
      gsap.fromTo(particle, { y: window.innerHeight + 100, opacity: 0 },
        { y: -100, opacity: 1, duration: gsap.utils.random(15, 25), ease: "none", onComplete: () => particle.remove() });
    };
    particleInterval.current = setInterval(() => {
      if (particleFieldRef.current && particleFieldRef.current.children.length < 15) createParticle();
    }, 2000);
    return () => clearInterval(particleInterval.current);
  }, []);

  // Hover tilt
  const handleCardMouseMove = useCallback((e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const rotateX = ((y - rect.height / 2) / (rect.height / 2)) * 15;
    const rotateY = ((x - rect.width / 2) / (rect.width / 2)) * 15;
    gsap.to(card, { rotationX: -rotateX, rotationY: rotateY, transformPerspective: 1000, duration: 0.2 });
  }, []);

  const handleCardMouseLeave = useCallback((e) => {
    gsap.to(e.currentTarget, { rotationX: 0, rotationY: 0, duration: 0.6, ease: "back.out(1.7)" });
  }, []);

  // Button hover
  const handleButtonMouseEnter = useCallback((e) => {
    gsap.to(e.currentTarget, { y: -3, scale: 1.02, duration: 0.3, ease: "power2.out" });
  }, []);
  const handleButtonMouseLeave = useCallback((e) => {
    gsap.to(e.currentTarget, { y: 0, scale: 1, duration: 0.3, ease: "power2.out" });
  }, []);

  // Explosion effect
  const createButtonExplosion = useCallback((button, path) => {
    const particles = 12;
    const rect = button.getBoundingClientRect();
    for (let i = 0; i < particles; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: fixed;
        top: ${rect.top + rect.height / 2}px;
        left: ${rect.left + rect.width / 2}px;
        width: 4px; height: 4px;
        background: var(--gold); border-radius: 50%; pointer-events: none; z-index: 1000;
      `;
      document.body.appendChild(particle);
      const angle = (360 / particles) * i;
      const velocity = 80 + Math.random() * 80;
      gsap.to(particle, {
        x: Math.cos(angle * Math.PI / 180) * velocity,
        y: Math.sin(angle * Math.PI / 180) * velocity,
        scale: 0, opacity: 0, duration: 1, ease: "power2.out",
        onComplete: () => particle.remove()
      });
    }
    setTimeout(() => navigate(path), 600); // navigate after animation
  }, [navigate]);

  return (
    <div className="timeline-container" ref={timelineContainerRef}>
      <div className="particle-field" ref={particleFieldRef}></div>

      <div className="timeline-header" ref={timelineHeaderRef}>
        <h1 className="timeline-title">
          <span className="title-word" ref={el => titleWordsRef.current[0] = el}>Growth-Focused </span>
          <span className="title-word" ref={el => titleWordsRef.current[1] = el}>Marketing, </span>
          <span className="title-word" ref={el => titleWordsRef.current[2] = el}>Delivered</span>
        </h1>
        <p className="timeline-subtitle">We provide performance-driven marketing services that solve real challenges and accelerate your business growth.</p>
        <div className="header-glow" ref={headerGlowRef}></div>
      </div>

      <div className="timeline-wrapper">
        <div className="timeline-content">
          {services.map((service, index) => (
            <div className="timeline-item" key={service.title} ref={el => timelineItemsRef.current[index] = el}>
              <div className="timeline-card"
                ref={el => timelineCardsRef.current[index] = el}
                onMouseMove={handleCardMouseMove}
                onMouseLeave={handleCardMouseLeave}>
                <div className="card-background"></div>
                <div className="card-content">
                  <span className="card-badge">{service.badge}</span>
                  <div className="card-icon-wrapper">
                    <div className="card-icon"><img src={service.icon} alt={service.title} /><div className="icon-glow"></div></div>
                  </div>
                  <h3 className="card-title">{service.title}</h3>
                  <p className="card-description">{service.description}</p>
                  <button
                    className="cta-button"
                    onClick={(e) => createButtonExplosion(e.currentTarget, service.path)}
                    onMouseEnter={handleButtonMouseEnter}
                    onMouseLeave={handleButtonMouseLeave}>
                    <span className="button-text">Discover More</span>
                    <div className="button-particles"></div>
                  </button>
                </div>
                <div className="card-glow"></div>
              </div>
            </div>
            
          ))}
        </div>
      </div>
    </div>
  );
};

export default AwardWinningTimeline;
